// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * All the Canvas 2D drawing: the live 2-axis bubble level and the angle dial on
 * screen, plus a high-resolution standalone report card rendered off-screen for
 * the PNG export / share / clipboard.
 */

import type { Measurement } from './types';
import { formatDeg, formatGrade } from './format';

const INK = '#eef4ea';
const DIM = '#8ba08b';
const GRID = 'rgba(140,170,140,0.16)';
const SIGNAL = '#6fe257';
const SIGNAL_SOFT = 'rgba(111,226,87,0.16)';
const AMBER = '#ffb454';
const BG = '#0b0f0a';
const BG_CARD = '#080b07';

/** Set a canvas's backing store to match its CSS size × devicePixelRatio. */
function fit(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; w: number; h: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
  const rect = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width));
  const h = Math.max(1, Math.round(rect.height));
  if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  }
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, w, h };
}

/**
 * A round 2-axis bubble level. The bubble drifts to the HIGH side (opposite
 * gravity), like a real vial. Green inside tolerance, amber outside. `maxDeg`
 * sets the tilt that pins the bubble to the rim.
 */
export function drawBubble(
  canvas: HTMLCanvasElement,
  tiltX: number,
  tiltY: number,
  tolerance = 0.4,
  maxDeg = 12,
): void {
  const { ctx, w, h } = fit(canvas);
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const R = Math.min(w, h) / 2 - 8;

  // Vial background.
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fillStyle = BG;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = GRID;
  ctx.stroke();

  // Crosshair.
  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - R, cy);
  ctx.lineTo(cx + R, cy);
  ctx.moveTo(cx, cy - R);
  ctx.lineTo(cx, cy + R);
  ctx.stroke();

  // Concentric tolerance rings.
  const tol = Math.max(0.15, tolerance);
  const inTol = Math.hypot(tiltX, tiltY) <= tol;
  const ringR = (tol / maxDeg) * R;
  ctx.beginPath();
  ctx.arc(cx, cy, Math.max(10, ringR), 0, Math.PI * 2);
  ctx.strokeStyle = inTol ? SIGNAL : 'rgba(255,180,84,0.5)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Bubble — offset to the high side, clamped to the rim.
  const scale = R / maxDeg;
  let bx = -tiltX * scale;
  let by = -tiltY * scale;
  const bd = Math.hypot(bx, by);
  const maxD = R - 12;
  if (bd > maxD) {
    bx = (bx / bd) * maxD;
    by = (by / bd) * maxD;
  }
  const bubbleR = Math.max(12, R * 0.28);
  const grad = ctx.createRadialGradient(cx + bx - bubbleR / 3, cy + by - bubbleR / 3, 2, cx + bx, cy + by, bubbleR);
  const col = inTol ? SIGNAL : AMBER;
  grad.addColorStop(0, col);
  grad.addColorStop(1, inTol ? 'rgba(111,226,87,0.55)' : 'rgba(255,180,84,0.5)');
  ctx.beginPath();
  ctx.arc(cx + bx, cy + by, bubbleR, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = inTol ? '#a9f090' : '#ffd39a';
  ctx.stroke();
}

/**
 * A protractor-style dial for the clinometer/angle mode: a fixed horizon and a
 * rotated "surface" line at the measured slope, with a shaded angle wedge.
 */
export function drawDial(canvas: HTMLCanvasElement, angleDeg: number): void {
  const { ctx, w, h } = fit(canvas);
  ctx.clearRect(0, 0, w, h);
  const cx = w / 2;
  const cy = h / 2;
  const R = Math.min(w, h) / 2 - 8;

  // Base disc.
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fillStyle = BG;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = GRID;
  ctx.stroke();

  // Tick marks every 15°.
  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  for (let a = 0; a < 360; a += 15) {
    const rad = a * (Math.PI / 180);
    const r1 = R - (a % 45 === 0 ? 12 : 7);
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(rad) * r1, cy + Math.sin(rad) * r1);
    ctx.lineTo(cx + Math.cos(rad) * R, cy + Math.sin(rad) * R);
    ctx.stroke();
  }

  // Horizon reference.
  ctx.strokeStyle = DIM;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.moveTo(cx - R, cy);
  ctx.lineTo(cx + R, cy);
  ctx.stroke();
  ctx.setLineDash([]);

  // Angle wedge + slope line (tilt upward to the right).
  const a = Math.max(0, Math.min(90, angleDeg)) * (Math.PI / 180);
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.arc(cx, cy, R * 0.5, 0, -a, true);
  ctx.closePath();
  ctx.fillStyle = SIGNAL_SOFT;
  ctx.fill();

  ctx.strokeStyle = SIGNAL;
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(cx - Math.cos(a) * R, cy + Math.sin(a) * R);
  ctx.lineTo(cx + Math.cos(a) * R, cy - Math.sin(a) * R);
  ctx.stroke();

  // Pivot dot.
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, Math.PI * 2);
  ctx.fillStyle = SIGNAL;
  ctx.fill();
}

export interface CardMeta {
  headline: string;
  sub: string;
}

/**
 * Render a shareable 1200×675 report card off-screen and return a PNG blob. The
 * latest reading is shown large; up to eight captured measurements are listed.
 */
export async function renderReportCard(measurements: Measurement[], meta: CardMeta): Promise<Blob> {
  const W = 1200;
  const H = 675;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = BG_CARD;
  ctx.fillRect(0, 0, W, H);

  // Header.
  ctx.fillStyle = INK;
  ctx.textAlign = 'left';
  ctx.font = '600 34px system-ui, -apple-system, sans-serif';
  ctx.fillText('Tiltwell', 56, 74);
  ctx.fillStyle = DIM;
  ctx.font = '20px system-ui, -apple-system, sans-serif';
  ctx.fillText('angle & level report · tiltwell.benrichardson.dev', 56, 104);

  // Big headline reading.
  ctx.fillStyle = SIGNAL;
  ctx.font = '700 104px ui-monospace, monospace';
  ctx.fillText(meta.headline, 56, 224);
  ctx.fillStyle = AMBER;
  ctx.font = '600 30px ui-monospace, monospace';
  ctx.fillText(meta.sub, 60, 268);

  // Measurements table.
  const rows = measurements.slice(0, 8);
  let y = 336;
  ctx.font = '600 18px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = DIM;
  ctx.fillText('CAPTURED', 56, y);
  ctx.textAlign = 'right';
  ctx.fillText('GRADE · PITCH · RATIO', W - 56, y);
  ctx.textAlign = 'left';
  y += 14;
  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(56, y);
  ctx.lineTo(W - 56, y);
  ctx.stroke();
  y += 30;

  if (!rows.length) {
    ctx.fillStyle = DIM;
    ctx.font = '20px system-ui, -apple-system, sans-serif';
    ctx.fillText('No captures yet — tilt and press Capture to add readings.', 56, y + 6);
  }
  for (const m of rows) {
    ctx.fillStyle = INK;
    ctx.font = '600 22px system-ui, -apple-system, sans-serif';
    const label = m.label || (m.mode === 'level' ? 'Tilt' : m.mode === 'angle' ? 'Slope' : 'Angle');
    ctx.fillText(`${label}`, 56, y);
    ctx.fillStyle = SIGNAL;
    ctx.font = '600 22px ui-monospace, monospace';
    ctx.fillText(formatDeg(m.angle), 300, y);
    ctx.fillStyle = DIM;
    ctx.font = '20px ui-monospace, monospace';
    ctx.textAlign = 'right';
    ctx.fillText(`${formatGrade(m.grade)}  ·  ${m.roofPitch}  ·  ${m.ratio}`, W - 56, y);
    ctx.textAlign = 'left';
    y += 40;
    if (y > H - 80) break;
  }

  ctx.fillStyle = DIM;
  ctx.font = '18px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Measured on-device. Nothing was uploaded.', 56, H - 30);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}
